import { KeyboardEvent, useState } from "react";

interface ChipSuggestionKeysOptions {
	/** Everything the suggestion source currently offers. */
	options: string[];
	/** What the user has typed so far. */
	draft: string;
	values: string[];
	/**
	 * Whether Enter may commit text that matched no suggestion. Industries and
	 * locations are matched by the scoring model ("Law Practice" scores as "Legal
	 * Services", "Asia (excl. China)" is read as written, PREC-4515), so a value
	 * the taxonomy has never heard of still works. The search filters built from
	 * the rubric keep only the countries the API's tables know.
	 */
	allowFreeText: boolean;
	onAdd: (value: string) => void;
	onChange: (values: string[]) => void;
}

export interface ChipSuggestionKeys {
	/** The options to render — empty once the user dismissed them with Escape. */
	suggestions: string[];
	/** Index of the keyboard-highlighted option, -1 while the pointer drives. */
	highlighted: number;
	handleKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
	/** Call when the draft changes, so a stale highlight cannot be committed. */
	clearHighlight: () => void;
}

/**
 * Keyboard handling shared by the tour's chip inputs: arrow keys walk the
 * suggestions, Enter takes one, Escape dismisses them, Backspace on an empty
 * input removes the last chip.
 *
 * What Enter commits when nothing is highlighted depends on the input: a
 * free-text one takes the typed value (the Lead Scoring editor offers it as the
 * first option too), a taxonomy-only one takes the top suggestion. Neither used
 * to happen on the taxonomy inputs — they had no key handling at all, so typing
 * a value and pressing Enter did nothing and the text just sat in the box, which
 * reads as "the autocomplete is missing" (PREC-3719).
 */
export const useChipSuggestionKeys = ({
	options,
	draft,
	values,
	allowFreeText,
	onAdd,
	onChange,
}: ChipSuggestionKeysOptions): ChipSuggestionKeys => {
	const [activeIndex, setActiveIndex] = useState(-1);
	const [isDismissed, setIsDismissed] = useState(false);

	const suggestions = isDismissed ? [] : options;
	const highlighted = activeIndex < suggestions.length ? activeIndex : -1;

	const clearHighlight = () => {
		setActiveIndex(-1);
		setIsDismissed(false);
	};

	/** What Enter commits: the highlighted option, the draft, or the top option. */
	const pickValue = (): string | null => {
		if (highlighted >= 0) {
			return suggestions[highlighted];
		}

		if (allowFreeText) {
			// Typed in another case, a value the list knows is taken in the list's spelling, so
			// "germany" does not end up beside "Germany" (PREC-4515).
			const typed = draft.trim().toLowerCase();

			return suggestions.find((option) => option.toLowerCase() === typed) ?? draft;
		}

		return suggestions.length > 0 ? suggestions[0] : null;
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "ArrowDown" && suggestions.length > 0) {
			event.preventDefault();
			setActiveIndex((index) => (index + 1) % suggestions.length);

			return;
		}

		if (event.key === "ArrowUp" && suggestions.length > 0) {
			event.preventDefault();
			setActiveIndex((index) => (index <= 0 ? suggestions.length : index) - 1);

			return;
		}

		if (event.key === "Escape" && suggestions.length > 0) {
			event.preventDefault();
			setIsDismissed(true);
			setActiveIndex(-1);

			return;
		}

		if (event.key === "Enter" || event.key === ",") {
			event.preventDefault();

			const value = pickValue();

			if (value) {
				onAdd(value);
			}

			clearHighlight();

			return;
		}

		if (event.key === "Backspace" && draft === "" && values.length > 0) {
			onChange(values.slice(0, -1));
		}
	};

	return { suggestions, highlighted, handleKeyDown, clearHighlight };
};
